import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteDoc, doc, getDoc, limit, onSnapshot, orderBy, query, setDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { slugify } from '../../utils/slugify';
import { autoFormatMarkdownFromPlainText } from '../../utils/autoFormatMarkdown';

function normaliseWhitespace(value) {
  return String(value || '').replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function AdminSlotReviewsManager() {
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const [editingId, setEditingId] = useState('');
  const [draft, setDraft] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'slotReviews'), orderBy('createdAt', 'desc'), limit(200));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(next);
        setLoadingList(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Admin slotReviews list failed', err);
        setItems([]);
        setLoadingList(false);
      }
    );
    return () => unsub();
  }, []);

  const beginEdit = (item) => {
    setEditingId(item.id);
    setSaveStatus('');
    setDraft({
      title: item.title || '',
      provider: item.provider || '',
      imageUrl: item.imageUrl || '',
      body: item.body || '',
      createdAt: item.createdAt || null,
    });
  };

  const cancelEdit = () => {
    setEditingId('');
    setDraft(null);
    setSaveStatus('');
    setSaving(false);
  };

  const saveEdit = async () => {
    if (!editingId || !draft) return;
    setSaving(true);
    setSaveStatus('Saving…');
    try {
      const nextTitle = normaliseWhitespace(draft.title);
      const targetId = slugify(nextTitle) || editingId;

      // If the title changed, migrate doc id so URL stays title-based.
      if (targetId !== editingId) {
        const targetRef = doc(db, 'slotReviews', targetId);
        const targetExisting = await getDoc(targetRef);
        if (targetExisting.exists()) {
          setSaveStatus('Save failed: another review already uses this title/URL.');
          return;
        }

        const sourceRef = doc(db, 'slotReviews', editingId);
        await setDoc(targetRef, {
          title: nextTitle,
          provider: normaliseWhitespace(draft.provider),
          imageUrl: normaliseWhitespace(draft.imageUrl),
          body: normaliseWhitespace(autoFormatMarkdownFromPlainText(draft.body) || draft.body),
          createdAt: draft.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        await deleteDoc(sourceRef);
      } else {
        await updateDoc(doc(db, 'slotReviews', editingId), {
          title: nextTitle,
          provider: normaliseWhitespace(draft.provider),
          imageUrl: normaliseWhitespace(draft.imageUrl),
          body: normaliseWhitespace(autoFormatMarkdownFromPlainText(draft.body) || draft.body),
          updatedAt: serverTimestamp(),
        });
      }

      setSaveStatus('Saved.');
      setEditingId('');
      setDraft(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Saving slot review edit failed', err);
      setSaveStatus('Save failed. Check Firestore rules.');
    } finally {
      setSaving(false);
    }
  };

  const migrateToSlug = async (item) => {
    const title = normaliseWhitespace(item.title);
    const targetId = slugify(title);
    if (!targetId) {
      alert('Cannot create slug: missing title.');
      return;
    }
    if (targetId === item.id) return;

    const ok = window.confirm(`Migrate this review URL to /slots/${targetId} ?`);
    if (!ok) return;

    try {
      const targetRef = doc(db, 'slotReviews', targetId);
      const existing = await getDoc(targetRef);
      if (existing.exists()) {
        alert('Cannot migrate: another review already uses this title/URL.');
        return;
      }

      await setDoc(targetRef, {
        title,
        provider: normaliseWhitespace(item.provider),
        imageUrl: normaliseWhitespace(item.imageUrl),
        body: normaliseWhitespace(autoFormatMarkdownFromPlainText(item.body) || item.body),
        sourceText: normaliseWhitespace(item.sourceText),
        createdAt: item.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await deleteDoc(doc(db, 'slotReviews', item.id));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Migrate slot review failed', err);
      alert('Migration failed. Check Firestore rules.');
    }
  };

  const deleteItem = async (item) => {
    const ok = window.confirm(`Delete slot review "${item.title || item.id}"?`);
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'slotReviews', item.id));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Delete slot review failed', err);
      alert('Delete failed. Check Firestore rules.');
    }
  };

  return (
    <div className="admin-tab">
      <section className="content-card" aria-label="Manage slot reviews">
        <div className="content-card-header">
          <div>
            <div className="section-title">Slots review</div>
            <h2 className="section-subtitle">All slot reviews</h2>
          </div>
          <div className="secondary-text">{items.length} items</div>
        </div>

        {loadingList && <p className="secondary-text">Loading slot reviews…</p>}
        {!loadingList && !items.length && <p className="secondary-text">No slot reviews found.</p>}

        <div className="admin-list">
          {items.map((item) => {
            const isEditing = editingId === item.id;
            const thumb = item.imageUrl || '';
            return (
              <div key={item.id} className="admin-row">
                <div className="admin-row-main">
                  <div className="admin-row-thumb" aria-hidden="true">
                    {thumb ? <img src={thumb} alt="" loading="lazy" /> : null}
                  </div>
                  <div className="admin-row-meta">
                    <div className="admin-row-title">{item.title || 'Slot review'}</div>
                    <div className="secondary-text">{item.provider || '—'}</div>
                    <div className="secondary-text">
                      <Link to={`/slots/${item.slug || item.id}`} className="latest-see-all">
                        Open →
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="admin-row-actions">
                  {!isEditing ? (
                    <>
                      <button type="button" className="header-outline-button" onClick={() => beginEdit(item)}>
                        Edit
                      </button>
                      {item.title && slugify(item.title) && slugify(item.title) !== item.id && (
                        <button
                          type="button"
                          className="header-outline-button"
                          onClick={() => migrateToSlug(item)}
                        >
                          Fix URL
                        </button>
                      )}
                      <button type="button" className="header-outline-button" onClick={() => deleteItem(item)}>
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="header-outline-button" onClick={cancelEdit} disabled={saving}>
                        Cancel
                      </button>
                      <button type="button" className="cta-button" onClick={saveEdit} disabled={saving}>
                        <span>{saving ? '…' : '✓'}</span> {saving ? 'Saving…' : 'Save'}
                      </button>
                    </>
                  )}
                </div>

                {isEditing && draft && (
                  <div className="admin-editor">
                    <div className="admin-editor-grid">
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Title</label>
                        <input
                          className="text-input"
                          value={draft.title}
                          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        />
                      </div>
                      <div className="mini-item admin-editor-field">
                        <label className="mini-label">Provider</label>
                        <input
                          className="text-input"
                          value={draft.provider}
                          onChange={(e) => setDraft({ ...draft, provider: e.target.value })}
                        />
                      </div>
                      <div className="mini-item admin-editor-field admin-editor-wide">
                        <label className="mini-label">Image URL</label>
                        <input
                          className="text-input"
                          value={draft.imageUrl}
                          onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
                        />
                      </div>
                      <div className="mini-item admin-editor-field admin-editor-wide">
                        <label className="mini-label">Body</label>
                        <textarea
                          className="text-input"
                          rows={10}
                          value={draft.body}
                          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                        />
                      </div>
                    </div>

                    {saveStatus && (
                      <p className="secondary-text" style={{ marginTop: 10 }}>
                        {saveStatus}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default AdminSlotReviewsManager;


